import { useEffect, useId, useRef, useState } from 'react';
import { ArrowUpIcon, CheckIcon, PencilIcon } from 'lucide-react';
import type { AskUserToolOptionDTO } from '@shared/schemas/chat-ask-user';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export const ASK_USER_ROW_ATTRIBUTE = 'data-ask-user-row';

const ROW_CLASS = [
  'group/row flex w-full items-center gap-3 rounded-xl border border-transparent px-2.5 py-2 text-left text-sm',
  'outline-none transition-colors duration-150',
  'hover:border-border hover:bg-muted/50',
  'focus-visible:border-ring focus-visible:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring/40',
  'disabled:pointer-events-none disabled:opacity-60',
].join(' ');

const KEY_CHIP_CLASS = 'inline-flex size-5 shrink-0 items-center justify-center rounded-md border border-border bg-background text-[11px] font-medium tabular-nums text-muted-foreground transition-colors group-hover/row:text-foreground group-focus-visible/row:text-foreground';

type OptionRowProps = {
  disabled: boolean;
  multiple: boolean;
  onClick: () => void;
  option: AskUserToolOptionDTO;
  selected: boolean;
  shortcut: number;
};

export function OptionRow({ disabled, multiple, onClick, option, selected, shortcut }: OptionRowProps) {
  const labelId = useId();
  const descriptionId = useId();
  const description = option.description?.trim();

  return (
    <button
      {...{ [ASK_USER_ROW_ATTRIBUTE]: '' }}
      type="button"
      role={multiple ? 'checkbox' : 'radio'}
      aria-checked={selected}
      aria-describedby={description ? descriptionId : undefined}
      aria-keyshortcuts={shortcut <= 9 ? String(shortcut) : undefined}
      aria-labelledby={labelId}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        ROW_CLASS,
        selected && 'border-primary/30 bg-primary/[0.06] hover:border-primary/40 hover:bg-primary/[0.08]',
      )}
    >
      <kbd aria-hidden className={KEY_CHIP_CLASS}>{shortcut}</kbd>
      <span className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span id={labelId} className="font-medium text-foreground">
          {option.label}
          {option.recommended ? (
            <span className="ml-1.5 inline-flex items-center rounded-full bg-primary/10 px-1.5 py-px align-middle text-[11px] font-medium text-primary">
              Recommandé
            </span>
          ) : null}
        </span>
        {description ? (
          <>
            <span aria-hidden className="hidden text-muted-foreground/60 sm:inline">·</span>
            <span id={descriptionId} className="text-muted-foreground">{description}</span>
          </>
        ) : null}
      </span>
      {multiple ? (
        <span
          aria-hidden
          className={cn(
            'inline-flex size-4 shrink-0 items-center justify-center rounded-[5px] border transition-colors',
            selected
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-border bg-background',
          )}
        >
          {selected ? <CheckIcon className="size-3" strokeWidth={3} /> : null}
        </span>
      ) : null}
    </button>
  );
}

type FreeTextRowProps = {
  disabled: boolean;
  onSubmit: (text: string) => void;
};

export function FreeTextRow({ disabled, onSubmit }: FreeTextRowProps) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const restoreFocusRef = useRef(false);

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    } else if (restoreFocusRef.current) {
      restoreFocusRef.current = false;
      triggerRef.current?.focus();
    }
  }, [open]);

  if (!open) {
    return (
      <button
        {...{ [ASK_USER_ROW_ATTRIBUTE]: '' }}
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        className={cn(ROW_CLASS, 'text-muted-foreground hover:text-foreground')}
      >
        <span aria-hidden className="inline-flex size-5 shrink-0 items-center justify-center">
          <PencilIcon className="size-3.5" />
        </span>
        <span>Autre réponse…</span>
      </button>
    );
  }

  return (
    <form
      className="t-ask-reveal flex w-full items-center gap-2 px-1.5 py-1"
      onSubmit={(event) => {
        event.preventDefault();
        const trimmed = value.trim();

        if (trimmed && !disabled) {
          onSubmit(trimmed);
        }
      }}
    >
      <Input
        ref={inputRef}
        aria-label="Votre réponse"
        autoComplete="off"
        type="text"
        disabled={disabled}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            restoreFocusRef.current = true;
            setValue('');
            setOpen(false);
          }
        }}
        placeholder="Précisez votre réponse…"
        className="h-9 flex-1 rounded-xl border-border/70 bg-background px-3 text-sm shadow-none"
      />
      <Button
        type="submit"
        size="icon-sm"
        disabled={disabled || !value.trim()}
        aria-label="Envoyer"
        className="shrink-0 rounded-xl"
      >
        <ArrowUpIcon className="size-4" strokeWidth={2.5} />
      </Button>
    </form>
  );
}

export function AskUserSkeleton() {
  return (
    <div aria-hidden className="flex flex-col gap-1">
      {[64, 48, 56].map((width) => (
        <div key={width} className="flex items-center gap-3 px-2.5 py-2">
          <div className="size-5 shrink-0 rounded-md bg-muted/70 motion-safe:animate-pulse" />
          <div
            className="h-3.5 rounded-md bg-muted/60 motion-safe:animate-pulse"
            style={{ width: `${width}%` }}
          />
        </div>
      ))}
    </div>
  );
}
